import { Link } from 'react-router-dom';
import { Truck, Wrench, Trash2, Mail, Phone, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

const socialLinks = [
  { icon: Phone, href: 'tel:+491631945793', label: 'Telefon' },
  { icon: Mail, href: 'mailto:info@panthera-umzug.de', label: 'E-Mail' },
  { icon: Facebook, href: 'https://www.facebook.com/share/1G9kWPwhQZ/?mibextid=wwXIfr', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/panthera.umzug.berlin', label: 'Instagram' },
  { icon: RedditIcon, href: 'https://www.reddit.com/u/Panthera-umzug/s/52d3cYz6Nc', label: 'Reddit' },
];

export const Footer = () => {
  const { t } = useLanguage();

  const serviceLinks = [
    { icon: Truck, label: t.nav.umzug, path: '/umzug-berlin' },
    { icon: Wrench, label: t.nav.montage, path: '/moebelmontage-berlin' },
    { icon: Trash2, label: t.nav.entruempelung, path: '/entruempelung-berlin' },
  ];

  return (
    <footer className="bg-black border-t border-[#2a2a2a]" data-testid="main-footer">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <Link to="/">
              <img src="https://customer-assets.emergentagent.com/job_d4e1a410-67d7-4e44-b13c-32da4b8a1883/artifacts/my73mjfj_WhatsApp%20Image%202025-12-21%20at%2020.34.25.jpeg" alt="Panthera Umzug Berlin" className="h-14 w-auto mb-4" loading="lazy" width="56" height="56" />
            </Link>
            <p className="text-gray-400 text-sm mb-5">{t.footer.tagline}</p>
            <div className="flex items-center gap-2">
              {socialLinks.map((s, i) => {
                const Icon = s.icon;
                return (
                  <a key={i} href={s.href} target={s.href.startsWith('http') ? '_blank' : undefined} rel={s.href.startsWith('http') ? 'noopener noreferrer' : undefined} aria-label={s.label} className="w-9 h-9 bg-[#1a1a1a] rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-[#D4AF37] hover:to-[#8F6B1F] transition-all duration-300 group">
                    <Icon className="w-4 h-4 text-gray-400 group-hover:text-black" />
                  </a>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold mb-5">{t.footer.services}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={i}>
                    <Link to={s.path} className="text-gray-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm">
                      <Icon className="w-4 h-4" /> {s.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-5">{t.footer.quickLinks}</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">{t.footer.home}</Link></li>
              <li><Link to="/kontakt" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">{t.nav.kontakt}</Link></li>
              <li><a href="#impressum" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">{t.footer.impressum}</a></li>
              <li><a href="#datenschutz" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm">{t.footer.datenschutz}</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-5">{t.footer.contact}</h3>
            <ul className="space-y-3 text-sm">
              <li><a href="tel:+491631945793" className="text-gray-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2"><Phone className="w-4 h-4" /> +49 163 1945793</a></li>
              <li><a href="mailto:info@panthera-umzug.de" className="text-gray-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2"><Mail className="w-4 h-4" /> info@panthera-umzug.de</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#2a2a2a] pt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div id="impressum">
              <h4 className="text-white font-bold mb-3 text-sm">{t.footer.impressumTitle}</h4>
              <p className="text-gray-500 text-xs leading-relaxed whitespace-pre-line">{t.footer.impressumText}</p>
            </div>
            <div id="datenschutz">
              <h4 className="text-white font-bold mb-3 text-sm">{t.footer.datenschutzTitle}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{t.footer.datenschutzText}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2a2a2a] mt-8 pt-6 text-center">
          <p className="text-gray-600 text-xs">{t.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
};
